import React, { useState } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress, Paper } from "@mui/material";

import { LoadingButton, Progress } from "ui";

import { selectLayerByVersionAndLayerKey, useLazyGetFeaturesQuery } from "fond/api/layersSlice";
import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { LayerStatus } from "fond/hooks/useGetImportLayers";
import BomModal from "fond/project/bom/BomModal";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { MuPowerOutputLayerIds, MuPowerSolverLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { ConfirmModal, LinearProgressWithLabel, LogViewerModal } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  power: { label: "Generating power design" },
  bom: { label: "Generating bill of materials" },
};

interface PowerSolverPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
  solverLayers: Map<string, LayerStatus>;
  project: Project;
}

const PowerSolverPanel: React.FC<PowerSolverPanelProps> = ({ isReadOnly, canRun, solverLayers, project }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);
  const [fetchFeatures] = useLazyGetFeaturesQuery();

  const primaryWiresLayer = useAppSelector((state) =>
    selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerOutputLayerIds.primaryWires })
  );
  const secondaryWiresLayer = useAppSelector((state) =>
    selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerOutputLayerIds.secondaryWires })
  );

  const { workflowState, stages, runWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER,
  });
  const { value: isCustomWorkflowErrorLogsEnabled } = useFeatureFlag("custom_workflow_error_logs");

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const isAnyUploading = Array.from(solverLayers.values()).some(
    (layer) => layer.status && ["CONVERTING", "IMPORTING", "BUILDING_TILES"].includes(layer.status)
  );

  const allRequiredUploaded = !!solverLayers.get(MuPowerSolverLayerIds.sourceHubs)?.layer;

  const performLock = async () => {
    setShowLockConfirm(false);
    setIsLocking(true);

    const layers = [
      { outputLayer: primaryWiresLayer, targetKey: MuPowerSolverLayerIds.primaryFootprint },
      { outputLayer: secondaryWiresLayer, targetKey: MuPowerSolverLayerIds.secondaryFootprint },
    ];

    await Promise.all(
      layers.map(async ({ outputLayer, targetKey }) => {
        if (!outputLayer?.ID) return;
        const { data: features } = await fetchFeatures({ layerId: outputLayer.ID });
        if (!features?.length) return;

        const file = new File([JSON.stringify(featureCollection(features))], `${targetKey.split("/").pop()}.geojson`, {
          type: "application/geo+json",
        });

        await dispatch(
          addImport({
            versionId,
            layerKey: targetKey,
            displayName: targetKey.split("/").pop() || targetKey,
            files: [file],
          })
        );
      })
    );

    setIsLocking(false);
  };

  const handleLockFootprint = () => {
    const hasPrimaryFootprint = !!solverLayers.get(MuPowerSolverLayerIds.primaryFootprint)?.layer;
    const hasSecondaryFootprint = !!solverLayers.get(MuPowerSolverLayerIds.secondaryFootprint)?.layer;

    if (hasPrimaryFootprint || hasSecondaryFootprint) {
      setShowLockConfirm(true);
    } else {
      performLock();
    }
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation required</AlertTitle>
          Please complete data preparation before running the power solver.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mt: 2 }}>
          <AlertTitle>Power solver failed</AlertTitle>
          There was an error running the power solver. Please try again.
          {isCustomWorkflowErrorLogsEnabled && (
            <Box mt={1}>
              {logs?.type === "waiting" && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={12} color="inherit" />
                  Attempting to process logs
                </Box>
              )}
              {logs?.type === "ready" && logs.logs.length === 0 && "Error logs are not available."}
              {logs?.type === "ready" && logs.logs.length > 0 && (
                <Button size="small" variant="outlined" color="error" onClick={() => setIsLogsModalOpen(true)}>
                  View Logs
                </Button>
              )}
            </Box>
          )}
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mt: 2 }}>
          Power design complete.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <LoadingButton
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          loading={isLocking}
          disabled={isReadOnly || isAnyUploading || isInProgress}
          onClick={handleLockFootprint}
        >
          Lock footprints
        </LoadingButton>
      )}

      {workflowState === "COMPLETE" && (
        <Button fullWidth variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setIsBomModalOpen(true)}>
          View bill of materials
        </Button>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      <Button
        fullWidth
        variant="contained"
        size="small"
        sx={{ mt: 2 }}
        disabled={!runWorkflow || !canRun || !allRequiredUploaded || isInProgress || isAnyUploading || isReadOnly}
        onClick={() => runWorkflow?.()}
      >
        {workflowState === "COMPLETE" ? "Rerun" : "Start"}
      </Button>

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      <ConfirmModal
        open={showLockConfirm}
        content="The Primary Footprint and/or Secondary Footprint layers already have data. Importing will replace the existing data. Continue?"
        confirmText="Continue"
        onConfirm={performLock}
        onCancel={() => setShowLockConfirm(false)}
      />

      {isCustomWorkflowErrorLogsEnabled && logs?.type === "ready" && (
        <LogViewerModal open={isLogsModalOpen} onClose={() => setIsLogsModalOpen(false)} logs={logs.logs} title="Logs" />
      )}

      {isBomModalOpen && <BomModal project={project} architecture={null} onClose={() => setIsBomModalOpen(false)} />}
    </>
  );
};

export default PowerSolverPanel;
